import { createHtmlElementFromString, getResourceTilePlaceholderIconForType, formatText } from '../../utils/html.utils';
import {
  ageFilterNewId,
  ageFilterUpdatedId,
  HamiltonProductReduced,
  typeFilterDocumentId,
  typeFilterElearningId,
  typeFilterKbId,
  typeFilterPublicationId,
  typeFilterQuoteId,
  typeFilterVideoId,
  typeFilterScientificPublicationId,
  typeFilterAudioId
} from '../../api/products-api';
import { currentBreakpoint } from '../../utils/resize.utils';
import { FirstLevelMenuItem, SecondLevelMenuItem } from '../../api/menu-api';
import { taxonomies } from '../../taxonomies.module';
import { PageModule } from '../../utils/page.module';
import { header } from '../header/header.module';
import { resourcesCenter, ResourcesView } from './resources-center.module';
import { Param, readUrlParameter, writeToUrl } from '../../utils/url.utils';
import { TaxonomyCategory } from '../../api/taxonomies-api';
import { scrollToTop } from '../../utils/helper.utils';
import {
  HamiltonResourceBaseType,
  HamiltonResourceDocument,
  HamiltonResourceProduct,
  HamiltonResourceResult,
  HamiltonResourceResults,
  ResourceType
} from '../../api/search-api';
import { resourceTile } from '../tiles/resource-tile/resource-tile.module';
import { getAllResources, getResourceDetailPageTypeFromResourceType } from '../../api/resources-api';
import { addItemToFavorites, deleteItemFromFavoritesList } from '../../api/favorites-api';
import { toastNotification } from '../toast-notification/toast-notification.module';
import { favoritesList } from '../favorites-list/favorites-list.module';
import { FavoritesId, favoritesListData } from '../../favorites-list-data.module';
import { breakpointListener } from '../../utils/breakpoint-listener.module';
import { isScrollingAllowed } from '../../utils/body-scroll.utils';
import {getCatalogDetailPageFromUrl, getCatalogDetailPageUrl, ResourceDetailPageType} from '../../api/general-api';
import { socialShare } from '../../components/social-share/social-share.module';

/**
 * Initializes and renders the resources.
 */
export class ResourcesList extends PageModule {
  /**
   * Selector of resources view
   */
  componentSelector = '.resources-list-view';

  /**
   * Markup for the resources view
   */
  template = `<div class="resources-list-view">
      <aside class="filter__aside filter__aside--light">
        <div class="filter-sidebar">
        </div>
    </aside>
    <div class="filter filter--dark filter--open" id="resources-center-filter"> 
      <div class="filter__content">
        <div class="resource-center-list"></div>
        <div class="indicators indicators--loading">
          <span class="indicator indicator--active"></span>
          <span class="indicator indicator--active"></span>
          <span class="indicator indicator--active"></span>
        </div>
      </div>
    </div>
  </div>`;

  /**
   * Template for the language dropdown of the filter.
   */
  languageDropdownTemplate = `
  <div class="filter-section">
      <label class="form-control__label-outside" for="filter-dropdown"></label>
      <div class="form-control form-control--dropdown">
          <!--<button class="button button&#45;&#45;dropdown" id="filter-dropdown"></button>-->
          <input type="hidden" name="form-filter-dropdown" value="en">
          <input type="text" class="button button--dropdown button--dropdown__editable" id="filter-dropdown"/>
          <ul class="dropdown" data-reference-id="filter-dropdown">
          </ul>
      </div>
        
      <input type="checkbox" class="checkbox" id="resource-center-language-filter" name="resource-center-language-filter" value="0" checked="false">
      <label class="label text-color-grey" for="resource-center-language-filter"></label>
  </div>`;

  /**
   * Template for a row in the language dropdown of the filter.
   */
  languageDropdownItemTemplate = '<li class="dropdown__item" tabindex="0"></li>';

  /**
   * Stores the resource results.
   */
  resourceResults: HamiltonResourceResults = null;

  /**
   * List of currently selected taxonomies IDs (from the filter).
   */
  currentFilters: string[] = [];

  /**
   * Current search phrase that the user typed into the search field.
   */
  currentSearchPhrase: string = null;

  /**
   * The ventilator ID that the user selected in the ventilator dropdown in the header.
   */
  currentVentilatorId: string = null;

  /**
   * Currently selected first level menu item.
   */
  currentFirstLevelMenuItem: FirstLevelMenuItem | FirstLevelMenuItem[] = null;

  /**
   * Currently selected second level menu item.
   */
  currentSecondLevelMenuItem: SecondLevelMenuItem | SecondLevelMenuItem[] = null;

  /**
   * Parameter that filters the available resources and must be specified with each API call.
   */
  paths = '';

  languageList: {
    /**
     * The language code of the language that should
     * be shown in the language dropdown initially.
     */
    defaultLanguage: string;

    /**
     * Key is a 2 digit language code (e.g. 'en'), the
     * value is the translated name of the language
     * that needs to be displayed in the language dropdown.
     */
    languages: { [key: string]: string }[];
  };

  /**
   * Variable to prevent multiple calls to load more resources. Is set to false
   * as long as new resources are loaded from the API.
   */
  isLoadingProducts = false;
  /**
   * Variable that indicates if all data that is necessary for this page has been loaded already.
   * E.g. if the taxonomy categories and menu items have been loaded. This is helpful for only
   * rendering the filter when all data is available.
   */
  wasInitialized = false;
  /**
   * Keeps track of how many resources are loaded/shown at the moment.
   */
  currentNumberOfResults = 10;

  /**
   * Number of resources to load when the user reaches the end of the product list.
   */
  resultNumberStepSize = 10;

  /**
   * When the user reaches the bottom of the page minus this distance in px, new resources
   * get loaded automatically.
   */
  loadingDistanceToBottom = 400;

  /**
   * Number of resources that should be loaded in the beginning on different breakpoints. Is also the
   * step size which determines how many resources will be loaded on scroll when the user reaches the end
   * of the page.
   */
  numberOfProductsToDisplay = {
    large: 20,
    desktop: 9,
    tablet: 6,
    phone: 4
  };

  /**
   * Prefix that is used for IDs in the language filter dropdown.
   */
  languageDropdownIdPrefix = 'language-dropdown-option-';
  /**
   * ID for the 'all' option inside the language filter dropdown.
   */
  languageDropdownIdAllLanguages = 'all';

  /**
   * The currently selected language, that is used to filter resources.
   */
  currentResourceLanguage: string;

  /**
   * If the checkbox "include resources in English" below the
   * language dropdown is checked or not.
   */
  includesResourcesInEnglish = false;

  /**
   * The taxonomy categories.
   */
  taxonomyCategories: TaxonomyCategory[] = [];

  /**
   * URL to retrieve article detail pages.
   */
  articlesUrl: string;

  /**
   * URL to retrieve resources detail pages.
   */
  resourcesUrl: string;

  /**
   * Markup for a section in the filter.
   * Contains an accordion that toggles the filter options (taxonomies)
   * that belong to this filter section (taxonomy category).
   */
  filterSectionMarkup = `
<div class="filter-section">
    <div class="accordion accordion--filter">
        <button class="accordion__header"></button>
        <div class="accordion__content">
            <div class="interactive-tags interactive-tags--multiselect">
            </div>
        </div>
    </div>
</div>`;

  /**
   * Empty state for the resource tiles when no tiles are available.
   */
  resourcesEmptyState = `
<div class="resources-list-view__empty-state">
    <h4 class="resources-list-view__empty-state-title"><span class="text-color-light"></span></h4>
    <span class="resources-list-view__empty-state-message"></span>
    <div class="button-section button-section--small-margin">
      <button class="button">
        <span class="button__text"></span>
      </button>
    </div>
</div>`;

  /**
   * Markup for the input element of a filter option.
   */
  filterTagInputMarkup = '<input type="checkbox">';

  /**
   * Markup for the label element of a filter option.
   */
  filterTagLabelMarkup = '<label><span></span><span class="tag tag--counter"></span></label>';

  /** 
   * Filters reset button.
   */
  resetFiltersButtonMarkup = `
  <div class="filter-section">
    <div class="button-section button-section--small-margin">
      <button class="button filter-header__reset-button">
        <span class="button__text"></span>
      </button>
    </div>
  </div>`;

  supportedLanguages = ['en', 'de', 'fr', 'es', 'it', 'zh', 'pt', 'tr', 'ja', 'ru'];
  lastListRequest = 0;
  /**
   * Setup the component with translations
   * @param translations The translations to initialize
   * @param paths Paths parameter for product-list requests.
   * @param languageList: List of languages to display in the language dropdown (filter).
   * @param articlesUrl URL that can be used to retrieve the contents of the detail pages for articles.
   * @param resourcesUrl URL that can be used to retrieve the contents of the detail pages for resources.
   */
  constructor(
    translations: { [key: string]: string },
    paths: string,
    languageList: {
      languages: { [key: string]: string }[];
      defaultLanguage: string;
    },
    articlesUrl: string,
    resourcesUrl: string
  ) {
    super();
    this.translations = translations;
    this.paths = paths;
    this.languageList = languageList;
    this.articlesUrl = articlesUrl;
    this.resourcesUrl = resourcesUrl;
  }

  /**
   * Update the number of products that should be loaded depending on the current breakpoint
   */
  resetNumberOfProductsBeingLoaded(): void {
    const breakpoint = currentBreakpoint();
    if (breakpoint.isLarge) {
      this.currentNumberOfResults = this.numberOfProductsToDisplay.large;
      this.resultNumberStepSize = this.numberOfProductsToDisplay.large;
    } else if (breakpoint.isDesktop) {
      this.currentNumberOfResults = this.numberOfProductsToDisplay.desktop;
      this.resultNumberStepSize = this.numberOfProductsToDisplay.desktop;
    } else if (breakpoint.isTablet) {
      this.currentNumberOfResults = this.numberOfProductsToDisplay.tablet;
      this.resultNumberStepSize = this.numberOfProductsToDisplay.tablet;
    } else {
      this.currentNumberOfResults = this.numberOfProductsToDisplay.phone;
      this.resultNumberStepSize = this.numberOfProductsToDisplay.phone;
    }
  }

  /**
   * Create the resource center. Fetches the resource information and renders the resource tiles.
   */
  renderView(): void {
    // show the sub nav
    header.showSubNav(true);

    // Hide the headers shadow before user scrolled
    header.setHideShadowBeforeScrolled(false);

    // set the page title
    document.title = this.translations.headerTitle;

    // Add to top button with translation
    const content = this.pageComponent.querySelector<HTMLElement>('.filter__content');
    content.dataset.toTop = this.translations.toTop;

    // set loading state until the filters are loaded
    this.renderFilterLoadingState();
    this.setupFilterTranslations();

    // now fetch the products
    this.resetNumberOfProductsBeingLoaded();
    this.showLoadingIndicator(true);
    this.updateFilterPosition();

    // First of all retrieve the taxonomies and their categories
    taxonomies.getCachedTaxonomyCategories().then(async (taxonomyCategories) => {
      this.taxonomyCategories = taxonomyCategories;
      this.loadFiltersFromUrl();

      // check if the URL contains a language that should be
      // selected in the filter language dropdown
      this.loadSelectedResourceLanguageFilter();

      // now fetch the products
      this.fetchResultData(this.resultNumberStepSize).then((products) => {
        if (!this.wasInitialized) {
          this.setupFilter();
        }

        this.wasInitialized = true;
        this.resourceResults = products;
        this.renderResourceTiles();

        // create listener for infinite scrolling
        this.setupInfiniteScrollListener();
        this.renderFilter();

        formatText(this.pageComponent);
      });
    });
  }

  /**
   * Updates the height and position of the filter and filter content area to fill the remaining
   * space below the main navigation and tab bars.
   */
  updateFilterPosition(): void {
    // update the filter position
    const breakpoint = currentBreakpoint();
    const isMobile = breakpoint.isMobilePortrait || breakpoint.isMobileLandscape;
    if (!isMobile) {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const mainNavigationSticky =
        document.querySelector<HTMLElement>('.main-navigation-wrapper').style.position === 'sticky';
      scrollTop === 0
        ? header.updateFilterPositionDocumentTop()
        : mainNavigationSticky
        ? header.updateFilterPositionScrolledUp()
        : header.updateFilterPositionScrolledDown();
    }
  }

  /**
  * Reset all filters. Removes all filters that can be selected in the filter sidebar. Updates the URL and re-renders the product results.
  * @param keepLanguage: Boolean if the language should be cleared or not
  * @param clearVentilator: Boolean if the ventilator selection should be cleared or not
  */
  resetFilters(keepLanguage = false, clearVentilator = false): void {
    this.currentFilters = [];

    let parameters = null;
    let removeParams = [];
    
    this.updateFiltersInUrl();

    if(!keepLanguage) {
      // also remove the selected resource language
      this.currentResourceLanguage = this.languageDropdownIdAllLanguages;
      parameters = { param: Param.RESOURCE_LANGUAGE, value: this.languageDropdownIdAllLanguages }
    }

    if(clearVentilator) {
      // clear the ventilator selection
      removeParams.push('ventilator');
      header.updateVentilatorDropdownSelection('default_all');
    }

    writeToUrl(parameters, removeParams, true, true);
    
    this.renderView();
  }

  /**
   * Fill the filter element with the translated labels.
   */
  setupFilterTranslations(): void {
    const filterElement = this.pageComponent.querySelector<HTMLElement>('.filter');
    filterElement.dataset.labelButton = this.translations.filterShowAll;
    filterElement.dataset.labelFilter = this.translations.filter;
    filterElement.dataset.labelReset = this.translations.mobileResetFilter;
  }

  /**
   * If the current URL includes any filters, load them so only the filtered products are shown to the user.
   */
  loadFiltersFromUrl(): void {
    const filtersFromUrl = readUrlParameter(Param.FILTER) || '';
    // collect the filters that need to be displayed
    const filterCategories = this.getFilterCategoriesForCurrentMenuSelection();
    this.currentFilters = [];

    // manually add the age filter IDs because they are not
    // part of the taxonomies
    if (filtersFromUrl.includes(ageFilterNewId)) {
      this.currentFilters.push(ageFilterNewId);
    }
    if (filtersFromUrl.includes(ageFilterUpdatedId)) {
      this.currentFilters.push(ageFilterUpdatedId);
    }
    if (filtersFromUrl.includes(typeFilterDocumentId)) {
      this.currentFilters.push(typeFilterDocumentId);
    }
    if (filtersFromUrl.includes(typeFilterElearningId)) {
      this.currentFilters.push(typeFilterElearningId);
    }
    if (filtersFromUrl.includes(typeFilterKbId)) {
      this.currentFilters.push(typeFilterKbId);
    }
    if (filtersFromUrl.includes(typeFilterQuoteId)) {
      this.currentFilters.push(typeFilterQuoteId);
    }
    if (filtersFromUrl.includes(typeFilterVideoId)) {
      this.currentFilters.push(typeFilterVideoId);
    }
    if (filtersFromUrl.includes(typeFilterPublicationId)) {
      this.currentFilters.push(typeFilterPublicationId);
    }
    if (filtersFromUrl.includes(typeFilterScientificPublicationId)) {
      this.currentFilters.push(typeFilterScientificPublicationId);
    }
    if (filtersFromUrl.includes(typeFilterAudioId)) {
      this.currentFilters.push(typeFilterAudioId);
    }

    for (const taxonomyCategory of filterCategories) {
      for (const taxonomy of taxonomyCategory.taxonomies) {
        const taxonomyId = taxonomy['@id'];
        if (filtersFromUrl.includes(taxonomyId)) {
          this.currentFilters.push(taxonomyId);
        }
      }
    }

    // load the ventilator from the URL
    const ventilatorId = readUrlParameter(Param.VENTILATOR);
    if (ventilatorId) {
      this.currentVentilatorId = ventilatorId;
    }

    // finally clear the URL filters that are not applicable anymore
    this.updateFiltersInUrl(true, true);
  }

  /**
   * Check if the URL contains a language code, that should be selected in the
   * language filter dropdown of the resource filter.
   */
  loadSelectedResourceLanguageFilter(): void {
    const resourceLanguageFromUrl = readUrlParameter(Param.RESOURCE_LANGUAGE);

    if (resourceLanguageFromUrl) {
      this.currentResourceLanguage = resourceLanguageFromUrl;
    } else if (this.languageList.defaultLanguage) {
      // If no language in URL -> select the default language if available
      this.currentResourceLanguage = this.languageList.defaultLanguage;
      writeToUrl({ param: Param.RESOURCE_LANGUAGE, value: this.currentResourceLanguage }, null, true);
    }

    this.checkIfIncludeEnglishResourcesNeedToBeIncluded();
  }

  /**
   * The "include resources in English" checkbox should be selected by default only if
   * the selected language is not English/all and there was no state selection in the URL
   */
  checkIfIncludeEnglishResourcesNeedToBeIncluded(): void {
    const includeResourcesEnFromUrl = readUrlParameter(Param.INCLUDE_RESOURCES_EN);
    const includeEnglishResourcesByDefault = false;
    includeResourcesEnFromUrl === 'true';
    this.includesResourcesInEnglish = includeEnglishResourcesByDefault;
  }

  /**
   * Get the filter category IDs for all filters that should be displayed for the current combination of selected
   * category and selected tab (first & second level menu item).
   */
  getFilterCategoriesForCurrentMenuSelection(): TaxonomyCategory[] {
    const filterCategories = [];

    if (!Array.isArray(this.currentFirstLevelMenuItem) && !Array.isArray(this.currentSecondLevelMenuItem)) {
      // If there is a tab selected (second level), then show filters for this tab
      if (this.currentSecondLevelMenuItem) {
        for (const categoryId of this.currentSecondLevelMenuItem.filters) {
          const filterCategory = this.taxonomyCategories.find((category) => category['@id'] === categoryId);
          if (filterCategory) {
            filterCategories.push(filterCategory);
          } else {
            /* eslint-disable no-console */
            console.error(
              `Could not find taxonomy for filter with ID ${categoryId}. Is this filter missing in the /taxonomies API?`
            );
          }
        }
      } else if (this.currentFirstLevelMenuItem) {
        // if no tab but a category is selected, then the 'all' option in the second level is selected.
        // Means we display all filters for all second level menu items
        const filterIds = new Set();
        for (const tab of this.currentFirstLevelMenuItem.secondaryCategories) {
          for (const categoryId of tab.filters) {
            filterIds.add(categoryId);
          }
        }

        for (const categoryId of filterIds) {
          const filterCategory = this.taxonomyCategories.find((category) => category['@id'] === categoryId);
          if (filterCategory) {
            filterCategories.push(filterCategory);
          } else {
            /* eslint-disable no-console */
            console.error(
              `Could not find taxonomy for filter with ID ${categoryId}. Is this filter missing in the /taxonomies API?`
            );
          }
        }
      } else {
        // if no category and no tab is selected, we display filters for 'all' categories and 'all' tabs
        const filterIds = new Set();
        for (const category of resourcesCenter.menuItems) {
          for (const tab of category.secondaryCategories) {
            for (const categoryId of tab.filters) {
              filterIds.add(categoryId);
            }
          }
        }

        for (const categoryId of filterIds) {
          const filterCategory = this.taxonomyCategories.find((category) => category['@id'] === categoryId);
          if (filterCategory) {
            filterCategories.push(filterCategory);
          } else {
            /* eslint-disable no-console */
            console.error(
              `Could not find taxonomy for filter with ID ${categoryId}. Is this filter missing in the /taxonomies API?`
            );
          }
        }
      }
    }

    filterCategories.sort((filter1, filter2) => {
      if (!filter1.order) filter1.order = 9999;
      if (!filter2.order) filter2.order = 9999;

      return parseFloat(filter1.order) - parseFloat(filter2.order);
    });

    return filterCategories;
  }

  /**
   * Create skeletons that display a loading state for the filter.
   */
  renderFilterLoadingState(): void {
    const filterContent = document.querySelector<HTMLElement>('.filter-sidebar');
    if (filterContent) {
      for (const child of filterContent.querySelectorAll('.filter-section')) {
        child.remove();
      }

      for (let i = 0; i < 3; i++) {
        const filterSection = createHtmlElementFromString(this.filterSectionMarkup);
        const accordion = filterSection.querySelector('.accordion');
        accordion.classList.add('accordion--skeleton');
        const tagContainer = filterSection.querySelector<HTMLElement>('.interactive-tags');
        tagContainer.classList.add('interactive-tags--skeleton');

        for (let i = 0; i < 4; i++) {
          this.createFilterTag('', null, false, false, '', '', tagContainer, true);
        }

        // finally append the filter section to the filter sidebar
        filterContent.appendChild(filterSection);
      }
    }
  }

  /**
   * Called when the user selected a ventilator from the ventilator dropdown.
   * @param ventilatorId: Id of the chosen ventilator.
   * @param reload: Boolean if the page should reload the products after the item changed.
   */
  async updateFilterWithNewVentilator(ventilatorId: string, reload = true): Promise<void> {
    if (this.currentVentilatorId !== ventilatorId) {
      this.currentVentilatorId = ventilatorId;

      if (reload && this.wasInitialized) {
        await this.updateFilterSettings();
        this.renderFilter();
      }
    }
  }

  /**
   * Called when the user typed in something in the search bar.
   * @param searchPhrase: Free text search from the user.
   * @param reload: Boolean if the page should reload the products after the item changed.
   */
  async updateFilterWithNewSearchPhrase(searchPhrase: string, reload = true): Promise<void> {
    if (this.currentSearchPhrase !== searchPhrase) {
      this.currentSearchPhrase = searchPhrase;

      if (reload && this.wasInitialized) {
        await this.updateFilterSettings();
        this.renderFilter();
      }
    }
  }

  /**
   * When the user selects a menu item from the first level, this function is called to update the products
   * to only those, that belong to the selected menu item.
   * @param firstLevelMenuItem: The selected menu item.
   * @param reload: Boolean if the page should reload the products after the item changed.
   */
  async updateFirstLevelMenuSelection(
    firstLevelMenuItem: FirstLevelMenuItem | FirstLevelMenuItem[],
    reload = true
  ): Promise<void> {
    this.currentFirstLevelMenuItem = firstLevelMenuItem;
    this.currentSecondLevelMenuItem = null;

    if (reload && this.wasInitialized) {
      await this.updateFilterSettings();
      this.renderFilter();
    }

    if (firstLevelMenuItem === null) {
      this.updateSecondLevelMenuSelection(null, reload);
    }
  }

  /**
   * When the user selects a menu item from the second level, this function is called to update the products
   * to only those, that belong to the selected menu item.
   *
   * @param secondLevelMenuItem: The selected menu item or null if the 'all' option was selected.
   * @param reload: Boolean if the page should reload the products after the item changed.
   */
  async updateSecondLevelMenuSelection(
    secondLevelMenuItem: SecondLevelMenuItem | SecondLevelMenuItem[],
    reload = true
  ): Promise<void> {
    this.currentSecondLevelMenuItem = secondLevelMenuItem;
    if (reload && this.wasInitialized) {
      await this.updateFilterSettings();
      this.renderFilter();
    }
  }

  /**
   * Load the products and filters depending on the current filter settings.
   */
  async updateFilterSettings(): Promise<void> {
    // first remove filters, that are not applicable to the current menu selection
    this.removeInapplicableFilters();

    // now fetch the products
    this.showLoadingIndicator(true);
    this.resetNumberOfProductsBeingLoaded();
    this.resourceResults = await this.fetchResultData(this.resultNumberStepSize);
    await this.renderResourceTiles();
    return;
  }

  /**
   * When the user switches between menus (categories) or sub menus (tabs), the
   * filters change. This function removes any filter from the URL and currentFilters
   * that are not applicable to the current menu selection anymore.
   */
  removeInapplicableFilters(): void {
    const filtersFromUrl = readUrlParameter(Param.FILTER) || '';
    // collect the filters for the current category/tab
    const filterCategories = this.getFilterCategoriesForCurrentMenuSelection();
    this.currentFilters = [];

    // we need to remove all filters that are not applicable to the current
    // menu selection from the url. This list contains only the valid filters
    // that will be added to the url after clearing it.
    const newFiltersForUrl = [];

    // manually add the age filter IDs because they are not
    // part of the taxonomies
    if (filtersFromUrl.includes(ageFilterNewId)) {
      this.currentFilters.push(ageFilterNewId);
      newFiltersForUrl.push(ageFilterNewId);
    }
    if (filtersFromUrl.includes(ageFilterUpdatedId)) {
      this.currentFilters.push(ageFilterUpdatedId);
      newFiltersForUrl.push(ageFilterUpdatedId);
    }

    if (filtersFromUrl.includes(typeFilterDocumentId)) {
      this.currentFilters.push(typeFilterDocumentId);
      newFiltersForUrl.push(typeFilterDocumentId);
    }
    if (filtersFromUrl.includes(typeFilterPublicationId)) {
      this.currentFilters.push(typeFilterPublicationId);
      newFiltersForUrl.push(typeFilterPublicationId);
    }
    if (filtersFromUrl.includes(typeFilterElearningId)) {
      this.currentFilters.push(typeFilterElearningId);
      newFiltersForUrl.push(typeFilterElearningId);
    }
    if (filtersFromUrl.includes(typeFilterKbId)) {
      this.currentFilters.push(typeFilterKbId);
      newFiltersForUrl.push(typeFilterKbId);
    }
    if (filtersFromUrl.includes(typeFilterQuoteId)) {
      this.currentFilters.push(typeFilterQuoteId);
      newFiltersForUrl.push(typeFilterQuoteId);
    }
    if (filtersFromUrl.includes(typeFilterVideoId)) {
      this.currentFilters.push(typeFilterVideoId);
      newFiltersForUrl.push(typeFilterVideoId);
    }
    if (filtersFromUrl.includes(typeFilterScientificPublicationId)) {
      this.currentFilters.push(typeFilterScientificPublicationId);
      newFiltersForUrl.push(typeFilterScientificPublicationId);
    }
    if (filtersFromUrl.includes(typeFilterAudioId)) {
      this.currentFilters.push(typeFilterAudioId);
      newFiltersForUrl.push(typeFilterAudioId);
    }

    // add all filters that are valid in the current selected category
    for (const taxonomyCategory of filterCategories) {
      for (const taxonomy of taxonomyCategory.taxonomies) {
        const taxonomyId = taxonomy['@id'];
        if (filtersFromUrl.includes(taxonomyId)) {
          this.currentFilters.push(taxonomyId);
          newFiltersForUrl.push(taxonomyId);
        }
      }
    }

    // finally clear the URL filters that are not applicable anymore
    this.updateFiltersInUrl(true, true);
  }

  /**
   * Setup listeners for the filter.
   */
  setupFilter(): void {
    // initialize listener for the reset buttons
    const resetButtons = document.querySelectorAll('.filter-header__reset-button');
    resetButtons.forEach(button => { button.addEventListener('click', () => {
      this.resetFilters();
    });});

    // listen for breakpoint changes between mobile and desktop
    breakpointListener.listenForBreakpointChangeBetweenMobileAndDesktop(() => {
      this.updateFilterPosition();
    });
  }

  /**
   * Render the filter sidebar for the given second level menu item.
   */
  renderFilter(): void {
    // Scroll to top, because products are reloaded
    scrollToTop();

    // set loading state until the filters are loaded
    this.renderFilterLoadingState();

    // To find out which filters we should display for the selected second level menu item,
    // we need to look up the taxonomies from the IDs that we get from the second level menu item.
    // First fetch all taxonomies/taxonomy categories. Then look up the necessary filters.

    // remove all existing filters
    const filterContent = document.querySelector<HTMLElement>('.filter-sidebar');
    if (filterContent) {
      for (const child of filterContent.querySelectorAll('.filter-section')) {
        child.remove();
      }

      // create the language dropdown
      this.createLanguagesDropdown(filterContent);

      if (readUrlParameter(Param.CATEGORY) !== '87bd3c09-484d-4ad5-8f4e-f96a15040377') {
        // collect the filters that need to be displayed
        const filterCategories = this.getFilterCategoriesForCurrentMenuSelection();
        const showFilterCategoriesWithNoResults = this.currentFilters?.length > 0;

        for (const taxonomyCategory of filterCategories) {
          const filterSection = createHtmlElementFromString(this.filterSectionMarkup);
          const tagContainer = filterSection.querySelector<HTMLElement>('.interactive-tags');
          const categoryLabel = taxonomyCategory.label || taxonomyCategory.name || taxonomyCategory['@name'];
          filterSection.querySelector<HTMLElement>('.accordion__header').innerText = categoryLabel;
          const categoryId = taxonomyCategory['@id'];

          // 'All' tag
          const allTagId = `${taxonomyCategory['@id']}-all`;

          // Total number of products that belong to this category
          const taxonomyCategoryCount = taxonomyCategory.taxonomies.reduce(
            (totalCount, taxonomy) => {
              if (taxonomy['@id'] && this.resourceResults && this.resourceResults.taxonomyCounts) {
                return totalCount + (this.resourceResults.taxonomyCounts[taxonomy['@id']] || 0)
              }
              return totalCount + 0;
            },
            0
          );

          // Check if the filter category has a selected filter, otherwise preselect the 'all' option
          const hasSelectedOption = taxonomyCategory.taxonomies.reduce(
            (hasActiveFilter, taxonomy) => hasActiveFilter || this.currentFilters.includes(taxonomy['@id']),
            false
          );

          if (!hasSelectedOption) { 
            filterSection.querySelector('.accordion--filter').classList.add('accordion--closed');
          }

          if (taxonomyCategoryCount > 0 || showFilterCategoriesWithNoResults) {
            // create the filter tag for 'all results'
            this.createFilterTag(
              this.translations.all,
              null,
              !hasSelectedOption,
              true,
              allTagId,
              categoryId,
              tagContainer
            );

            // and for each filter in this category create a filter tag
            for (const taxonomy of taxonomyCategory.taxonomies) {
              const taxonomyId = taxonomy['@id'];
              const taxonomyCount = this.resourceResults.taxonomyCounts[taxonomyId];
              const checked = this.currentFilters.includes(taxonomyId);
              const filterName = taxonomy.label || taxonomy.name || taxonomy['@name'];

              this.createFilterTag(filterName, taxonomyCount || 0, checked, false, taxonomyId, categoryId, tagContainer);
            }

            // finally append the filter section to the filter sidebar
            filterContent.appendChild(filterSection);
          }
        }
      }

      // The new/updated filters are not part of the API, so we need to add them manually
      this.createNewOrUpdatedFilterSection(filterContent);

      // Add the type filter
      if (readUrlParameter(Param.CATEGORY) !== '87bd3c09-484d-4ad5-8f4e-f96a15040377') {
        this.createTypeFilterSection(filterContent);
      }

      // update the mobile filter button state
      const mobileFilterButton = document.querySelector('.tag--filter');
      const mobileFilterResetButton = document.querySelector('.tag--filter-secondary');
      if (mobileFilterButton) {
        const noFiltersApplied =
          this.currentFilters?.length === 0 &&
          (this.currentResourceLanguage === this.languageDropdownIdAllLanguages || !this.currentResourceLanguage);

        mobileFilterButton.classList.toggle('tag--filter-inactive', noFiltersApplied);
        mobileFilterResetButton.classList.toggle('tag--hidden', noFiltersApplied);
      }

      // Reset filters button
      const resetFilters = createHtmlElementFromString(this.resetFiltersButtonMarkup);
      resetFilters.classList.add('filter-header__reset-button');
      resetFilters.querySelector<HTMLElement>('.button__text').innerText = this.translations.mobileResetFilter;
      filterContent.appendChild(resetFilters);

      resetFilters.addEventListener('click', () => {
        this.resetFilters(true, true);
      });
    }
  }

  /**
   * Create filter tags for the 'age' filter. Has options 'new' and 'updated'.
   * The new/updated filters are not part of the API, so we need to add them manually
   */
  createNewOrUpdatedFilterSection(filterContent: HTMLElement): void {
    const filterSection = createHtmlElementFromString(this.filterSectionMarkup);
    const tagContainer = filterSection.querySelector<HTMLElement>('.interactive-tags');
    filterSection.querySelector<HTMLElement>('.accordion__header').innerText = this.translations.ageFilterTitle;
    const categoryId = 'filter-age';

    // 'All' tag
    const allTagId = 'age-all';

    // Check if the filter category has a selected filter, otherwise preselect the 'all' option
    const hasSelectedOption =
      this.currentFilters.includes(ageFilterUpdatedId) || this.currentFilters.includes(ageFilterNewId);

    // create the filter tag for 'all results'
    this.createFilterTag(this.translations.all, null, !hasSelectedOption, true, allTagId, categoryId, tagContainer);

    // and for each filter in this category create a filter tag
    this.createFilterTag(
      this.translations.ageFilterNewLabel,
      null,
      this.currentFilters.includes(ageFilterNewId),
      false,
      ageFilterNewId,
      categoryId,
      tagContainer
    );
    this.createFilterTag(
      this.translations.ageFilterUpdatedLabel,
      null,
      this.currentFilters.includes(ageFilterUpdatedId),
      false,
      ageFilterUpdatedId,
      categoryId,
      tagContainer
    );

    // finally, append the filter section to the filter sidebar
    filterContent.appendChild(filterSection);
  }

  /**
   * Create filter tags for the 'type' filter.
   */
  createTypeFilterSection(filterContent: HTMLElement): void {
    const filterSection = createHtmlElementFromString(this.filterSectionMarkup);
    const tagContainer = filterSection.querySelector<HTMLElement>('.interactive-tags');
    filterSection.querySelector<HTMLElement>('.accordion__header').innerText = this.translations.typeFilterTitle;
    const categoryId = 'filter-type';

    // 'All' tag
    const allTagId = 'type-all';

    // Check if the filter category has a selected filter, otherwise preselect the 'all' option
    const hasSelectedOption =
      this.currentFilters.includes(typeFilterDocumentId) ||
      this.currentFilters.includes(typeFilterPublicationId) ||
      this.currentFilters.includes(typeFilterElearningId) ||
      this.currentFilters.includes(typeFilterKbId) ||
      this.currentFilters.includes(typeFilterQuoteId) ||
      this.currentFilters.includes(typeFilterVideoId) ||
      this.currentFilters.includes(typeFilterScientificPublicationId) ||
      this.currentFilters.includes(typeFilterAudioId);

    // create the filter tag for 'all results'
    this.createFilterTag(this.translations.all, null, !hasSelectedOption, true, allTagId, categoryId, tagContainer);

    // and for each filter in this category create a filter tag
    this.createFilterTag(
      this.translations.typeFilterDocumentLabel,
      null,
      this.currentFilters.includes(typeFilterDocumentId),
      false,
      typeFilterDocumentId,
      categoryId,
      tagContainer
    );

    this.createFilterTag(
      this.translations.typeFilterPresentationLabel,
      null,
      this.currentFilters.includes(typeFilterPublicationId),
      false,
      typeFilterPublicationId,
      categoryId,
      tagContainer
    );

    this.createFilterTag(
      this.translations.typeFilterKbLabel,
      null,
      this.currentFilters.includes(typeFilterKbId),
      false,
      typeFilterKbId,
      categoryId,
      tagContainer
    );

    this.createFilterTag(
      this.translations.typeFilterVideoLabel,
      null,
      this.currentFilters.includes(typeFilterVideoId),
      false,
      typeFilterVideoId,
      categoryId,
      tagContainer
    );

    this.createFilterTag(
      this.translations.typeFilterElearningLabel,
      null,
      this.currentFilters.includes(typeFilterElearningId),
      false,
      typeFilterElearningId,
      categoryId,
      tagContainer
    );

    this.createFilterTag(
      this.translations.typeFilterQuoteLabel,
      null,
      this.currentFilters.includes(typeFilterQuoteId),
      false,
      typeFilterQuoteId,
      categoryId,
      tagContainer
    );

    this.createFilterTag(
      this.translations.typeFilterScientificPublicationLabel,
      null,
      this.currentFilters.includes(typeFilterScientificPublicationId),
      false,
      typeFilterScientificPublicationId,
      categoryId,
      tagContainer
    );

    this.createFilterTag(
      this.translations.typeFilterAudioLabel,
      null,
      this.currentFilters.includes(typeFilterAudioId),
      false,
      typeFilterAudioId,
      categoryId,
      tagContainer
    );

    // finally, append the filter section to the filter sidebar
    filterContent.appendChild(filterSection);
  }

  /**
   * Create the customer dropdown for the filter.
   */
  createLanguagesDropdown(filterContent: HTMLElement): void {
    // render the customer dropdown and label
    const languageDropdown = createHtmlElementFromString(this.languageDropdownTemplate);
    languageDropdown.querySelector<HTMLElement>('.form-control__label-outside').innerText = this.translations.languages;
    const languageCheckbox = languageDropdown.querySelector<HTMLInputElement>('#resource-center-language-filter');
    const languageCheckboxLabel = languageDropdown.querySelector<HTMLElement>(
      '#resource-center-language-filter + label'
    );
    languageCheckboxLabel.innerText = this.translations.includeResourcesInEnglish;

    // For each language create an option in the dropdown
    const dropdownContainer = languageDropdown.querySelector('ul');
    // create an 'all' option
    const customerDropdownOption = createHtmlElementFromString(this.languageDropdownItemTemplate);
    customerDropdownOption.innerText = this.translations.all;
    customerDropdownOption.id = `${this.languageDropdownIdPrefix}${this.languageDropdownIdAllLanguages}`;
    dropdownContainer.appendChild(customerDropdownOption);

    for (const language of this.languageList.languages) {
      const customerDropdownOption = createHtmlElementFromString(this.languageDropdownItemTemplate);
      const [langCode, languageLabel] = Object.entries(language)[0];
      customerDropdownOption.innerText = languageLabel;
      customerDropdownOption.id = `${this.languageDropdownIdPrefix}${langCode}`;
      dropdownContainer.appendChild(customerDropdownOption);
    }

    const dropdownTitle = languageDropdown.querySelector<HTMLElement>('.button--dropdown');
    if (this.currentResourceLanguage) {
      const languageInfo = this.languageList.languages.find(
        (lang) => Object.keys(lang)[0] === this.currentResourceLanguage
      );
      if (languageInfo) {
        // if the requested language exists, retrieve the translation for UI
        const languageTranslation = languageInfo[Object.keys(languageInfo)[0]];

        // if there was a language set in the URL, select that language in the dropdown as well
        dropdownTitle.innerText = languageTranslation;
      } else {
        // If requested language (from URL) does not exist -> select all option
        dropdownTitle.innerText = this.translations.all;
      }
    } else {
      // If no option was preselected from URL or defaultLanguage
      dropdownTitle.innerText = this.translations.all;
    }

    // insert the customer dropdown at the top of the filter
    const firstFilterSection = filterContent.querySelector('.filter-section');
    if (firstFilterSection) {
      filterContent.insertBefore(languageDropdown, firstFilterSection);
    } else {
      filterContent.appendChild(languageDropdown);
    }

    // Listen for changes in the language dropdown
    dropdownContainer.addEventListener('onitemselect', async (event: CustomEvent) => {
      const selectedLangCode = event.detail.target.id.replace(this.languageDropdownIdPrefix, '');
      this.currentResourceLanguage = selectedLangCode;
      writeToUrl({ param: Param.RESOURCE_LANGUAGE, value: selectedLangCode });

      // user changed the language, now check if the "include English resources" checkbox
      // must be selected by default (e.g. for languages different from en/all)
      this.checkIfIncludeEnglishResourcesNeedToBeIncluded();

      await this.updateFilterSettings();
      this.renderFilter();
    });

    // Listen for changes in the language checkbox that includes English resources
    languageCheckbox.addEventListener('change', async () => {
      const includeEnglishResources = languageCheckbox.checked;
      this.includesResourcesInEnglish = includeEnglishResources;
      if (includeEnglishResources) {
        writeToUrl({ param: Param.INCLUDE_RESOURCES_EN, value: 'true' });
      } else {
        writeToUrl({ param: Param.INCLUDE_RESOURCES_EN, value: 'false' });
      }
      await this.updateFilterSettings();
      this.renderFilter();
    });

    // The "include resources in English" checkbox should only be shown when the user selected
    // a language that is different from English.
    const includeEnglishResourcesCheckboxNecessary =
      this.currentResourceLanguage &&
      this.currentResourceLanguage !== this.languageDropdownIdAllLanguages &&
      this.currentResourceLanguage !== 'en';
    languageCheckbox.classList.toggle('checkbox--hidden', !includeEnglishResourcesCheckboxNecessary);
    languageCheckbox.checked = this.includesResourcesInEnglish;
    setTimeout(() => {
      if (this.currentResourceLanguage === 'all') {
        document.querySelector('#filter-dropdown').value = this.translations.all;
      } else {
        const lang = this.languageList.languages.filter(v => Object.keys(v)[0] === this.currentResourceLanguage)[0];
        document.querySelector('#filter-dropdown').value = lang[this.currentResourceLanguage];
      }
    }, 0);
  }

  /**
   * Create a filter tag with the given properties and append it to the given tagContainer element.
   * @param label: Label of the filter option.
   * @param count: Number of results with this filter option. Displayed in a tag right next to the label.
   * @param checked: If this filter option should be checked or not.
   * @param allOption: If this filter option is an 'all' option that deselects all other options when selected.
   * @param id: ID for the input element.
   * @param categoryId: ID of the filter category (taxonomy category) that is used for the 'name' property of the input group.
   * @param tagContainer: HTML element that holds all tags that belong to the same filter group.
   * @param loadingState: If the tag should only represent a loading state.
   */
  createFilterTag(
    label: string,
    count: number,
    checked: boolean,
    allOption: boolean,
    id: string,
    categoryId: string,
    tagContainer: HTMLElement,
    loadingState = false
  ): void {
    const filterSuffix = '-filter';
    const filterId = id + filterSuffix;

    // If the filter option is currently selected
    const filterIsActive = this.currentFilters.includes(id);
    const filterIsDisabled = count === 0;

    // create the tag input element
    const filterTagInput = createHtmlElementFromString(this.filterTagInputMarkup) as HTMLInputElement;
    filterTagInput.toggleAttribute('checked', checked || filterIsActive);
    filterTagInput.name = categoryId;
    filterTagInput.value = filterId;
    filterTagInput.id = filterId;
    filterTagInput.disabled = filterIsDisabled;

    // mark the 'all' option
    if (allOption) filterTagInput.dataset.checkAll = 'true';

    // append the input to the tag container
    tagContainer.appendChild(filterTagInput);

    if (!loadingState) {
      // listen for changes of the checked state
      filterTagInput.addEventListener('change', (event) => {
        const currentTarget = event.currentTarget as HTMLElement;
        const taxonomyId = currentTarget.id.replace(filterSuffix, '');
        if (filterTagInput.checked) {
          // filter was selected
          if (currentTarget.dataset.checkAll === 'true') {
            // if 'all' option selected -> remove all filters from this taxonomy category
            const options = currentTarget.parentElement.querySelectorAll<HTMLInputElement>('input:not(:first-child)');
            for (const option of options) {
              this.currentFilters = this.currentFilters.filter(
                (taxonomyId) => taxonomyId !== option.id.replace(filterSuffix, '')
              );
            }
            this.updateFiltersInUrl();
          } else {
            // add the filter to the current set of filters
            this.currentFilters.push(taxonomyId);

            if (taxonomyId === ageFilterNewId && this.currentFilters.includes(ageFilterUpdatedId)) {
              // if the 'new' option of the age filter was selected and the 'updated' option is also selected,
              // remove the updated option because they are mutually exclusive
              this.currentFilters = this.currentFilters.filter((item) => item !== ageFilterUpdatedId);
            } else if (taxonomyId === ageFilterUpdatedId && this.currentFilters.includes(ageFilterNewId)) {
              // and the other way round
              this.currentFilters = this.currentFilters.filter((item) => item !== ageFilterNewId);
            }

            if (taxonomyId === typeFilterDocumentId) {
              this.currentFilters = this.currentFilters.filter((item) => {
                return (
                  item !== typeFilterElearningId &&
                  item !== typeFilterPublicationId &&
                  item !== typeFilterKbId &&
                  item !== typeFilterQuoteId &&
                  item !== typeFilterVideoId &&
                  item !== typeFilterScientificPublicationId &&
                  item !== typeFilterAudioId
                );
              });
            }
            if (taxonomyId === typeFilterElearningId) {
              this.currentFilters = this.currentFilters.filter((item) => {
                return (
                  item !== typeFilterDocumentId &&
                  item !== typeFilterPublicationId &&
                  item !== typeFilterKbId &&
                  item !== typeFilterQuoteId &&
                  item !== typeFilterVideoId &&
                  item !== typeFilterScientificPublicationId &&
                  item !== typeFilterAudioId
                );
              });
            }
            if (taxonomyId === typeFilterPublicationId) {
              this.currentFilters = this.currentFilters.filter((item) => {
                return (
                  item !== typeFilterDocumentId &&
                  item !== typeFilterElearningId &&
                  item !== typeFilterKbId &&
                  item !== typeFilterQuoteId &&
                  item !== typeFilterVideoId &&
                  item !== typeFilterScientificPublicationId &&
                  item !== typeFilterAudioId
                );
              });
            }
            if (taxonomyId === typeFilterKbId) {
              this.currentFilters = this.currentFilters.filter((item) => {
                return (
                  item !== typeFilterDocumentId &&
                  item !== typeFilterElearningId &&
                  item !== typeFilterPublicationId &&
                  item !== typeFilterQuoteId &&
                  item !== typeFilterVideoId &&
                  item !== typeFilterScientificPublicationId &&
                  item !== typeFilterAudioId
                );
              });
            }
            if (taxonomyId === typeFilterVideoId) {
              this.currentFilters = this.currentFilters.filter((item) => {
                return (
                  item !== typeFilterDocumentId &&
                  item !== typeFilterElearningId &&
                  item !== typeFilterPublicationId &&
                  item !== typeFilterKbId &&
                  item !== typeFilterQuoteId &&
                  item !== typeFilterScientificPublicationId &&
                  item !== typeFilterAudioId
                );
              });
            }
            if (taxonomyId === typeFilterQuoteId) {
              this.currentFilters = this.currentFilters.filter((item) => {
                return (
                  item !== typeFilterDocumentId &&
                  item !== typeFilterElearningId &&
                  item !== typeFilterPublicationId &&
                  item !== typeFilterKbId &&
                  item !== typeFilterVideoId &&
                  item !== typeFilterScientificPublicationId &&
                  item !== typeFilterAudioId
                );
              });
            }
            if (taxonomyId === typeFilterScientificPublicationId) {
              this.currentFilters = this.currentFilters.filter((item) => {
                return (
                  item !== typeFilterDocumentId &&
                  item !== typeFilterElearningId &&
                  item !== typeFilterPublicationId &&
                  item !== typeFilterKbId &&
                  item !== typeFilterVideoId &&
                  item !== typeFilterQuoteId &&
                  item !== typeFilterAudioId
                );
              });
            }
            if (taxonomyId === typeFilterAudioId) {
              this.currentFilters = this.currentFilters.filter((item) => {
                return (
                  item !== typeFilterDocumentId &&
                  item !== typeFilterElearningId &&
                  item !== typeFilterPublicationId &&
                  item !== typeFilterKbId &&
                  item !== typeFilterVideoId &&
                  item !== typeFilterQuoteId && 
                  item !== typeFilterScientificPublicationId
                );
              });
            }

            this.updateFiltersInUrl();
          }
        } else {
          // filter was removed, remove it from the current filters
          this.currentFilters = this.currentFilters.filter((item) => item !== taxonomyId);
          this.updateFiltersInUrl();
        }

        // finally reload the resource center
        this.renderView();
      });
    }

    // create the tag label
    const filterTagLabel = createHtmlElementFromString(this.filterTagLabelMarkup) as HTMLLabelElement;
    filterTagLabel.setAttribute('for', filterId);
    const labelSpan = filterTagLabel.querySelector('span');
    labelSpan.innerText = label;

    // create the tag counter
    const tagCounter = filterTagLabel.querySelector<HTMLElement>('.tag');
    tagCounter.innerText = count + '';

    if (count === null) {
      // remove the count tag if the count passed is null or zero
      // this is used for the 'all' tag for example
      tagCounter.parentElement.removeChild(tagCounter);
    }

    // append the label to the tag container
    tagContainer.appendChild(filterTagLabel);
  }

  /**
   * Update the current URL with the current filter settings.
   */
  updateFiltersInUrl(noHistory = false, noEvent = false): void {
    // write filters from the filter sidebar
    if (this.currentFilters.length > 0) {
      writeToUrl({ param: Param.FILTER, value: this.currentFilters.join(' ') }, null, noHistory, noEvent);
    } else {
      writeToUrl([], [Param.FILTER], noHistory, noEvent);
    }
  }

  /**
   * Update the already visible pin buttons (because wish list items have changed).
   */
  updatePinButtonStates(): void {
    if (this.pageComponent) {
      const pinButtonMobile = tile.querySelector<HTMLElement>('.tile__pin-button');
      const pinButtonDesktop = tile.querySelectorAll<HTMLElement>('.tile__pin-button')[1];
      favoritesListData
        .getCachedFavoritesListResourceIds(false)
        .then((resourcesInFavorites) => {
          const productTiles = this.pageComponent.querySelectorAll<HTMLElement>('.tile--resource');

          for (const tile of productTiles) {
            const resourceId = tile.dataset.resourceId;
            const isFavorite = favoritesListData.isFavorite(resourceId, resourcesInFavorites);

            pinButtonMobile.classList.toggle('button--primary', isFavorite);
            pinButtonDesktop.classList.toggle('button--primary', isFavorite);
          }
        })
        .catch(() => {
          // user logged out
          pinButtonMobile?.parentElement.removeChild(pinButtonMobile)
          pinButtonDesktop?.parentElement.removeChild(pinButtonDesktop)
        });
    }
  }

  /**
   * Renders the tiles for the resources (everything except products)
   * @param fromIndex: If provided this function only renders new resource tiles from the given index instead of replacing all existing rendered resource tiles.
   */
  async renderResourceTiles(fromIndex: number = null): Promise<void> {
    const resourceTilesGrid = this.pageComponent.querySelector<HTMLElement>('.resource-center-list');
    const resourceResults = this.resourceResults?.results;

    if (!fromIndex) {
      resourceTilesGrid.innerHTML = '';
    }

    if (resourceResults) {
      favoritesListData
        .getCachedFavoritesListResourceIds(false)
        .then((resourcesInFavorites) => {
          this.createResourceTiles(fromIndex, resourceResults, resourcesInFavorites, resourceTilesGrid);
        })
        .catch(() => {
          // user logged out
          this.createResourceTiles(fromIndex, resourceResults, null, resourceTilesGrid);
        });
    }

    if (!resourceResults || resourceResults.length === 0) {
      // render empty state
      this.renderProductsEmptyState(resourceTilesGrid, !!this.currentSearchPhrase);
    } else {
      this.removeEmptyState(resourceTilesGrid);
    }
  }

  /**
   * Create the resource tiles. Create a tile for each resource found for the current filter.
   * @param resourceTilesGrid: The grid that contains all resource tiles.
   * @param resourceResults: The resources to display.
   * @param fromIndex: If provided this function only renders new resources from the given index instead of replacing all existing rendered resources.
   * @param resourcesInFavorites: The list of resources that are currently part of the users favorite list or null if not logged in.
   */
  createResourceTiles(
    fromIndex: number,
    resourceResults: HamiltonResourceResult[],
    resourcesInFavorites: FavoritesId[],
    resourceTilesGrid: HTMLElement
  ): void {
    const startIndex = fromIndex || 0;
    for (let i = startIndex; i < resourceResults.length; i++) {
      const result = resourceResults[i];

      if (this.resourceResults.totalResults <= this.resultNumberStepSize) {
        // in case there are no more resources to load, immediately hide the loading indicator
        this.showLoadingIndicator(false);
      }

      let content = '';
      if (result.type === 'catalog-articles') {
        content = resourceTile.renderTileProduct(result as HamiltonResourceProduct, this.translations, true);
      } else {
        content = resourceTile.renderTileDocument(result as HamiltonResourceDocument, this.translations, true);
      }

      // create the tile
      const resourceTileElement = createHtmlElementFromString(content);
      if (result.type !== 'catalog-articles') {
        const imgElem = resourceTileElement.querySelector<HTMLElement>('img');
        if (imgElem) {
          imgElem.addEventListener('error', (e) => {
            const fallbackElement = createHtmlElementFromString(
              getResourceTilePlaceholderIconForType((result as HamiltonResourceDocument).tagValue)
            );
            if (
              (result as HamiltonResourceDocument).mimeType &&
              (result as HamiltonResourceDocument).mimeType.toLowerCase().indexOf('ppt') > -1
            ) {
              (e.currentTarget as HTMLImageElement).src =
                '/.resources/hamilton-theme/hamilton-medical-theme/webresources/css/images/ppt.jpg';
            } else {
              (e.currentTarget as HTMLElement).replaceWith(fallbackElement);
            }
          });
        }

        // set info message if product is not public
        if ((result as HamiltonResourceDocument).accessType === 'partnernet') {
          const socialShare = resourceTileElement.querySelector<HTMLElement>('.social-share');
          socialShare.classList.add('social-share--with-text');
          const info = createHtmlElementFromString(
            `<div class="social-share__info">${this.translations.accessInfo}</div>`
          );
          socialShare.insertBefore(info, socialShare.firstElementChild);
        }
      }
      if (resourcesInFavorites !== null) {
        // add the pin button
        this.setupPinButton(resourceTileElement, <HamiltonResourceDocument>result, resourcesInFavorites);
      } else {
        // remove the pin button
        const pinButtonMobile = resourceTileElement.querySelector('.tile__pin-button');
        const pinButtonDesktop = resourceTileElement.querySelectorAll('.tile__pin-button')[1];
        pinButtonMobile.parentElement.removeChild(pinButtonMobile);
        pinButtonDesktop.parentElement.removeChild(pinButtonDesktop);
      }

      // listen for clicks on the view buttons and redirect to the detail page
      this.setupDetailPageClickListenersForResourceTile(resourceTileElement, result);

      // finally append the resource tile to the grid
      resourceTilesGrid.appendChild(resourceTileElement);
    }

    // If there are so few items, that the document does not scroll,
    // the scroll listener (that fetches more products) will not be triggered.
    // Therefore check if the document does scroll at the moment and if not,
    // continue loading more products.
    const lastScrollTop = window.scrollY;
    const currentDocumentHeight = document.documentElement.clientHeight + lastScrollTop;
    const totalDocumentHeight = document.documentElement.scrollHeight;
    // when the menu is open, scroll is blocked on the body
    const scrollingAllowed = isScrollingAllowed();
    if (
      currentDocumentHeight === totalDocumentHeight &&
      scrollingAllowed &&
      resourcesCenter.currentView === ResourcesView.RESOURCES
    ) {
      this.loadMoreProducts();
    }
  }

  /**
   * Setup click listeners for the resource tiles. The view or detail buttons need to link to the detail page.
   */
  setupDetailPageClickListenersForResourceTile(tile: HTMLElement, resource: HamiltonResourceResult): void {
    const buttonMobile = tile.querySelector(`.${resourceTile.resourceTileDetailButtonClass}`);
    const buttonDesktop = tile.querySelectorAll(`.${resourceTile.resourceTileDetailButtonClass}`)[1];
    //const buttonQuickInfo = tile.querySelector('.button-quick-info');
    const buttonCopy = tile.querySelector(`.social-share__copy`) as HTMLElement;
    const socialShareElement = tile.querySelector(`.social-share-button`) as HTMLElement;
    const title = tile.querySelector('.tile__headline');
    const detailPageType = getResourceDetailPageTypeFromResourceType(resource.type);
    const detailButtonClickListenerEmbeddedPage = (): void => {
      writeToUrl([
        { param: Param.RESOURCE_DETAIL_TYPE, value: resource.type },
        { param: Param.RESOURCE_DETAIL_ID, value: resource.nodeid }
      ]);
    };
    const infoButtonClickListener = () => {
      const resourceLanguageFromUrl = readUrlParameter(Param.RESOURCE_LANGUAGE);
      const currentLocale = document.querySelector('body').dataset.locale;
      const encodedTitle = encodeURIComponent((resource.originalTitle||resource.title).replace(/[^a-zA-Z0-9\u00C0-\u017F\u0400-\u04FF\u3041-\u3096\u30A1-\u30FF\u4E00-\u9FFF]/g, '-'));
      let currentArticlesUrl = this.articlesUrl;
      if (this.supportedLanguages.indexOf(resourceLanguageFromUrl) > -1) {
        currentArticlesUrl = currentArticlesUrl.replace(`/${currentLocale}/`, `/${resourceLanguageFromUrl}/`);
        if (currentLocale === 'en') {
          currentArticlesUrl = currentArticlesUrl.replace(window.location.host + '/', `${window.location.host}/${resourceLanguageFromUrl}/`);
        }
      }

      const detailPageUrl = getCatalogDetailPageUrl(currentArticlesUrl, resource.nodeid, resource.type, encodedTitle);
      const modalContent: HTMLElement = document.getElementById('resource-quick-info-overlay').querySelector('.overlay__content');
      const modalBox: HTMLElement = document.getElementById('resource-quick-info-overlay').querySelector('.overlay');
      modalContent.innerHTML = '';
      modalBox.style.display = 'none'
      document.getElementById('resource-quick-info-overlay').classList.add('overlay--visible');
      getCatalogDetailPageFromUrl(this.resourcesUrl.replace('.html', ''), resource.nodeid, resource.type, ResourceDetailPageType.Resource, true).then((html) => {
        if (html.length > 0) {
          const div = document.createElement('div');
          div.innerHTML = html;
          const summary = div.querySelector('.summary-content-quick-info');
          modalContent.appendChild(summary);
          formatText(modalContent);
          modalBox.style.display = 'block';
        }
      });
    }
    const detailButtonClickListenerStandalonePage = (): void => {
      const resourceLanguageFromUrl = readUrlParameter(Param.RESOURCE_LANGUAGE);
      const currentLocale = document.querySelector('body').dataset.locale;

      const encodedTitle = encodeURIComponent((resource.originalTitle||resource.title).replace(/[^a-zA-Z0-9\u00C0-\u017F\u0400-\u04FF\u3041-\u3096\u30A1-\u30FF\u4E00-\u9FFF]/g, '-'));
      let currentArticlesUrl = this.articlesUrl;

      if (this.supportedLanguages.indexOf(resourceLanguageFromUrl) > -1) {
        currentArticlesUrl = currentArticlesUrl.replace(`/${currentLocale}/`, `/${resourceLanguageFromUrl}/`);
        if (currentLocale === 'en') {
          currentArticlesUrl = currentArticlesUrl.replace(window.location.host + '/', `${window.location.host}/${resourceLanguageFromUrl}/`);
        }
      }

      const detailPageUrl = getCatalogDetailPageUrl(currentArticlesUrl, resource.nodeid, resource.type, encodedTitle);
      window.location.href = detailPageUrl;
    };

    const copyButtonClickSetupEmbeddedPage = (): void => {
      const url = `${window.location.href}&${Param.RESOURCE_DETAIL_TYPE}=${resource.type}&${Param.RESOURCE_DETAIL_ID}=${resource.nodeid}`;

      buttonCopy.dataset.detailLink = url;
      socialShare.initialize(socialShareElement);
    }

    const copyButtonClickSetupStandalonePage = (): void => {
      const resourceLanguageFromUrl = readUrlParameter(Param.RESOURCE_LANGUAGE);
      const currentLocale = document.querySelector('body').dataset.locale;
      const encodedTitle = encodeURIComponent(resource.title.replace(/[^a-zA-Z0-9\u00C0-\u017F\u0400-\u04FF\u3041-\u3096\u30A1-\u30FF\u4E00-\u9FFF]/g, '-'));
      let currentArticlesUrl = this.articlesUrl;

      if (this.supportedLanguages.indexOf(resourceLanguageFromUrl) > -1) {
        currentArticlesUrl = currentArticlesUrl.replace(`/${currentLocale}/`, `/${resourceLanguageFromUrl}/`);
        if (currentLocale === 'en') {
          currentArticlesUrl = currentArticlesUrl.replace(window.location.host + '/', `${window.location.host}/${resourceLanguageFromUrl}/`);
        }
      }

      const detailPageUrl = getCatalogDetailPageUrl(currentArticlesUrl, resource.nodeid, resource.type, encodedTitle);
      buttonCopy.dataset.detailLink = detailPageUrl;
      socialShare.initialize(socialShareElement);
    }

    if (detailPageType && buttonDesktop) {
      if (resource.type === 'knowledge-base' || resource.type === 'news') {
        buttonMobile.addEventListener('click', detailButtonClickListenerStandalonePage);
        buttonDesktop.addEventListener('click', detailButtonClickListenerStandalonePage);
        title?.addEventListener('click', detailButtonClickListenerStandalonePage);
        copyButtonClickSetupStandalonePage();
      } else {
        buttonMobile.addEventListener('click', detailButtonClickListenerEmbeddedPage);
        buttonDesktop.addEventListener('click', detailButtonClickListenerEmbeddedPage);
        title?.addEventListener('click', detailButtonClickListenerEmbeddedPage);
        //buttonQuickInfo.addEventListener('click', infoButtonClickListener);
        copyButtonClickSetupEmbeddedPage();
      }
    }
  }

  /**
   * Listen for clicks on the pin button.
   */
  setupPinButton(
    tileElement: HTMLElement,
    resource: HamiltonResourceDocument,
    resourcesInFavorites: FavoritesId[]
  ): void {
    const pinButtonMobile = tileElement.querySelector('.tile__pin-button');
    const pinButtonDesktop = tileElement.querySelectorAll('.tile__pin-button')[1];
    const resourceId = resource.nodeid;
    const isInFavorites = favoritesListData.isFavorite(resource.nodeid, resourcesInFavorites);

    // make the pin button primary (blue) if the resource is already a favorite
    pinButtonMobile.classList.toggle('button--primary', isInFavorites);
    pinButtonDesktop.classList.toggle('button--primary', isInFavorites);

    const pinButtonClickListener = (): void => {
      // first of all check if the clicked resource is a favorite or not. This data is cached, so
      // if the users works in a different tab, then this data might be wrong. Pass forceUpdate: true,
      // if the data should be refreshed before adding/removing
      favoritesListData
        .getCachedFavoritesListResourceIds(false)
        .then((favoriteIds) => {
          const isFavorite = favoritesListData.isFavorite(resourceId, favoriteIds);
          const resourceFavoritesId = favoriteIds.find((favorite) => favorite.id === resourceId)?.favoritesItemId;

          if (isFavorite) {
            if (resourceFavoritesId) {
              // if the resource is a favorite -> remove it
              this.removeResourceFromFavorites(
                resource.title || resource.lead,
                resource.nodeid,
                resource.imageSmall,
                resourceFavoritesId,
                resource.type,
                resource.tagValue
              );
            }
          } else {
            // otherwise add it to favorites
            this.addResourceToFavorites(
              resource.title || resource.lead,
              resource.nodeid,
              resource.imageSmall,
              resource.type,
              resource.tagValue
            );
          }
        })
        .catch(() => {
          // user logged out
          pinButtonMobile?.parentElement.removeChild(pinButtonMobile)
          pinButtonDesktop?.parentElement.removeChild(pinButtonDesktop)
        });
    };

    // listen for clicks
    pinButtonMobile.addEventListener('click', pinButtonClickListener);
    pinButtonDesktop.addEventListener('click', pinButtonClickListener);
  }

  /**
   * Add the given resource to the list of favorites.
   */
  addResourceToFavorites(
    resourceTitle: string,
    resourceId: string,
    resourceImage: string,
    resourceType: HamiltonResourceBaseType,
    resourceTagValue: ResourceType
  ): void {
    addItemToFavorites(resourceId, resourceType).then((favoriteId) => {
      if (favoriteId) {
        const productInfo: HamiltonProductReduced = {
          nodeid: resourceId,
          partNumber: null,
          productName: resourceTitle,
          imageSmall: resourceImage,
          icons: [resourceTagValue]
        };

        const breakpoint = currentBreakpoint();
        if (breakpoint.isMobilePortrait || breakpoint.isMobileLandscape) {
          // on mobile show a toast
          toastNotification.createMobileToast(
            productInfo,
            this.translations.labelFavoritesListAdded,
            this.translations.favoritesGoTo,
            () => {
              favoritesList.openFavoritesList();
            }
          );
        } else {
          // Show toastify toast on desktop
          toastNotification.createDesktopToast(productInfo, this.translations.labelFavoritesListAdded);
        }

        // also add the item to the cached favorites list resource IDs and update the visible pin buttons in the resource tiles
        favoritesListData.addResourceToCachedFavoritesList(resourceId, favoriteId);
        this.updatePinButtonStates();
      } else {
        const errorMessage = this.translations.errorMessage;
        toastNotification.createErrorToastMessage(errorMessage);
      }
    });
  }

  /**
   * Remove the given resource from the list of favorites.
   */
  removeResourceFromFavorites(
    resourceTitle: string,
    resourceId: string,
    resourceImage: string,
    resourceFavoriteId: string,
    resourceType: HamiltonResourceBaseType,
    resourceTagValue: ResourceType
  ): void {
    deleteItemFromFavoritesList(resourceFavoriteId).then((success) => {
      if (success) {
        const productInfo: HamiltonProductReduced = {
          nodeid: resourceId,
          partNumber: null,
          productName: resourceTitle,
          imageSmall: resourceImage,
          icons: [resourceTagValue]
        };

        const breakpoint = currentBreakpoint();
        if (breakpoint.isMobilePortrait || breakpoint.isMobileLandscape) {
          // on mobile show a toast
          toastNotification.createMobileToast(
            productInfo,
            this.translations.labelFavoritesListRemoved,
            this.translations.favoritesGoTo,
            () => {
              favoritesList.openFavoritesList();
            }
          );
        } else {
          // Show toastify toast on desktop
          toastNotification.createDesktopToast(productInfo, this.translations.labelFavoritesListRemoved);
        }

        // also delete the item from the cached favorites list product IDs and update the visible pin buttons in the resource tiles
        favoritesListData.removeResourceFromCachedFavoritesList(resourceId);
        this.updatePinButtonStates();
      } else {
        const errorMessage = this.translations.errorMessage;
        toastNotification.createErrorToastMessage(errorMessage);
      }
    });
  }

  /**
   * Removes the empty state.
   */
  removeEmptyState(resourcesList: HTMLElement): void {
    resourcesList.classList.remove('resources-list-view--empty');
    const emptyStateElem = resourcesList.querySelector<HTMLElement>('.resources-list-view__empty-state');
    if (emptyStateElem) {
      emptyStateElem.parentElement.removeChild(emptyStateElem);
    }
  }

  /**
   * show or hide the loading indicator.
   */
  showLoadingIndicator(show: boolean): void {
    const loadingIndicator = document.querySelector<HTMLElement>('.filter .indicators--loading');
    if (loadingIndicator) {
      loadingIndicator.style.visibility = show ? 'visible' : 'hidden';
    }
  }

  /**
   * When the user scrolls to the bottom of the page, automatically load more items
   */
  setupInfiniteScrollListener(): void {
    let lastScrollTop = window.scrollY;

    let isThrottling = false;
    const scrollListener = (): void => {
      if (resourcesCenter.currentView === ResourcesView.RESOURCES) {
        const currentDocumentHeight = document.documentElement.clientHeight + lastScrollTop;
        const totalDocumentHeight = document.documentElement.scrollHeight;

        lastScrollTop = window.scrollY;
        if (!isThrottling) {
          setTimeout(() => {
            if (currentDocumentHeight >= totalDocumentHeight - this.loadingDistanceToBottom) {
              this.loadMoreProducts();
            }
            isThrottling = false;
          }, 16);
        }
        isThrottling = true;
      }
    };

    window.addEventListener('scroll', scrollListener, false);
  }

  /**
   * Call this function to load more products because the user scrolled to the end of the products list.
   */
  loadMoreProducts(): void {
    // now fetch the products
    if (!this.isLoadingProducts) {
      const moreProductsAvailable = this.currentNumberOfResults < this.resourceResults.totalResults;
      if (moreProductsAvailable) {
        this.isLoadingProducts = true;
        const newNumberOfProducts = this.currentNumberOfResults + this.resultNumberStepSize;
        this.fetchResultData(newNumberOfProducts).then(async (products) => {
          this.resourceResults.results = [...this.resourceResults.results, ...products.results];
          await this.renderResourceTiles(this.currentNumberOfResults);
          this.currentNumberOfResults += this.resultNumberStepSize;
          this.isLoadingProducts = false;
        });
      } else {
        // hide loading indicator
        this.showLoadingIndicator(false);
      }
    }
  }

  /**
   * Render the empty state inside the resources list, when no products are displayed.
   */
  renderProductsEmptyState(productGrid: HTMLElement, forSearch = true): void {
    this.removeEmptyState(productGrid);
    productGrid.classList.add('resources-list-view--empty');
    const emptyStateElement = createHtmlElementFromString(this.resourcesEmptyState);
    const subTitle = emptyStateElement.querySelector<HTMLElement>('.resources-list-view__empty-state-title span');
    const title = emptyStateElement.querySelector<HTMLElement>('.resources-list-view__empty-state-title');
    const message = emptyStateElement.querySelector<HTMLElement>('.resources-list-view__empty-state-message');
    const resetButton = emptyStateElement.querySelector<HTMLElement>('.button');
    const resetButtonLabel = resetButton.querySelector<HTMLElement>('span');

    // Insert translations
    if (forSearch) {
      // empty state when user searched
      const titleTextNode = document.createTextNode(this.translations.sorry + ' ');
      title.insertBefore(titleTextNode, subTitle);
      subTitle.innerText = this.translations.noResults;
      message.innerText = this.translations.searchForDifferentKeyword;
      resetButtonLabel.innerText = this.translations.resetSearch;
    } else {
      // empty state when no products available because of filters
      const titleTextNode = document.createTextNode(this.translations.sorryFilters + ' ');
      title.insertBefore(titleTextNode, subTitle);
      subTitle.innerText = this.translations.noResultsFiltered;
      message.innerText = this.translations.removeFilters;
      resetButtonLabel.innerText = this.translations.resetAllFilters;
    }

    // listen for clicks on the reset search button
    resetButton.addEventListener(
      'click',
      () => {
        if (this.currentSearchPhrase) {
          resourcesCenter.resetProductSearchInputField();
          resourcesCenter.removeSearchText();
        } else {
          this.resetFilters();
        }
      },
      false
    );

    // hide the loading indicator
    this.showLoadingIndicator(false);

    // and finally append the empty state to the tile grid
    productGrid.appendChild(emptyStateElement);
  }

  /**
   * Retrieve the results.
   */
  fetchResultData(limit: number): Promise<HamiltonResourceResults> {
    return new Promise((resolve) => {
      const offset = limit - this.resultNumberStepSize;
      const searchPhraseExisted = this.currentSearchPhrase !== null && this.currentSearchPhrase !== undefined;

      const languageCodes = [];
      // Check by which languages the resources should be filtered
      if (this.currentResourceLanguage && this.currentResourceLanguage !== this.languageDropdownIdAllLanguages) {
        languageCodes.push(this.currentResourceLanguage);
        if (this.includesResourcesInEnglish && this.currentResourceLanguage !== 'en') {
          languageCodes.push('en');
        }
      }
      let lastRequest = (new Date()).getTime();
      this.lastListRequest = lastRequest;
      getAllResources(
        this.currentFilters,
        this.currentSearchPhrase,
        Array.isArray(this.currentFirstLevelMenuItem)
          ? this.currentFirstLevelMenuItem.map((item) => item.id)
          : this.currentFirstLevelMenuItem?.id,
        Array.isArray(this.currentSecondLevelMenuItem)
          ? this.currentSecondLevelMenuItem.map((item) => item.id)
          : this.currentSecondLevelMenuItem?.id,
        this.paths,
        this.currentVentilatorId,
        languageCodes,
        this.resultNumberStepSize,
        offset
      ).then((results) => {
        if (searchPhraseExisted && results) {
          // If the user searched for something, update the number of results for each menu tab
          resourcesCenter.lastTaxonomyCounts = results.taxonomyCounts;
          resourcesCenter.lastTagCounts = results.tagCounts;
          resourcesCenter.updateSearchResultCountTags(results.taxonomyCounts, results.tagCounts);
        }
        if (this.lastListRequest === lastRequest) {
          resolve(results);
        }
      });
    });
  }
}
